/**
 * Drop-related utilities for SmswiserCampaignStatsTab.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';

/** Parse executeAfter "DD/MM/YYYY HH:mm" to timestamp. Returns NaN if invalid. */
export function parseExecuteAfter(executeAfter: string | null | undefined): number {
  const s = (executeAfter || '').trim();
  if (!s) return Number.NaN;
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})/);
  if (!m) return Number.NaN;
  const [, dd, mm, yyyy, hh, min] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(min), 0);
  return Number.isNaN(d.getTime()) ? Number.NaN : d.getTime();
}

function parseDropDate(s: string | null | undefined): number {
  if (!s?.trim()) return Number.NaN;
  const d = new Date(s.trim());
  return Number.isNaN(d.getTime()) ? Number.NaN : d.getTime();
}

/** Sort key for send order: finishedAt (sent) > executeAfter (scheduled) > createdAt. Oldest first. */
export function getDropOrderKey(row: DropsWithStatsRow): number {
  const finished = parseDropDate(row.drop.finishedAt);
  if (!Number.isNaN(finished)) return finished;
  const scheduled = parseExecuteAfter(row.drop.executeAfter);
  if (!Number.isNaN(scheduled)) return scheduled;
  const created = parseDropDate(row.drop.createdAt);
  return Number.isNaN(created) ? 0 : created;
}

/**
 * Logical date (YYYY-MM-DD) of a drop for grouping by work day.
 * Uses executeAfter > finishedAt > createdAt, matching getDropOrderKey.
 */
export function getDropLogicalDateISO(row: DropsWithStatsRow): string | null {
  const { drop } = row;
  if (drop.executeAfter?.trim()) {
    const m = drop.executeAfter.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m) {
      const [, dd, mm, yyyy] = m;
      return `${yyyy}-${mm!.padStart(2, '0')}-${dd!.padStart(2, '0')}`;
    }
  }
  if (drop.finishedAt?.trim()) {
    const d = new Date(drop.finishedAt.trim());
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  if (drop.createdAt?.trim()) {
    const d = new Date(drop.createdAt.trim());
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  return null;
}

export function orDash(v: string | string[] | null | undefined): string {
  if (v == null) return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  return String(v || '').trim() || '—';
}

/** Group key for stats grouping. Returns stable string for grouping rows. */
export function getGroupKey(row: DropsWithStatsRow, groupBy: string): string {
  if (groupBy === 'campaign') return orDash(row.drop.campaign);
  if (groupBy === 'leadsList') return orDash(row.drop.leadsList);
  if (groupBy === 'gateway') return orDash(row.drop.smsGatewayRoute);
  return '';
}
